import { forwardRef, type MouseEvent, RefObject } from 'react';
import styled from '@emotion/styled';
import {
  SvgIcon,
  SvgNames,
  tokens,
  Typography,
} from '@sunrun/experience-ui-components';
// eslint-disable-next-line import/no-restricted-paths
import { ListViewMenuOptions } from './ListViewMenuOptions';
import {
  ListViewColumnDef,
  ListViewSortDirection,
} from 'types/listViewColumns';
import { Prospect } from 'types/prospect';

type ListViewEqualHeaderProps<TSort extends string> = {
  columns: ListViewColumnDef<TSort>[];
  sortField: TSort;
  sortDirection: ListViewSortDirection;
  onSort: (field: TSort) => void;
};

const ListViewEqualHeader = <TSort extends string>({
  columns,
  sortField,
  sortDirection,
  onSort,
}: ListViewEqualHeaderProps<TSort>) => (
  <EqualHeaderRow>
    {columns.map(({ id, label, sortKey }) => (
      <EqualCell key={id}>
        {sortKey ? (
          <SortButton
            type="button"
            onClick={() => onSort(sortKey)}
            aria-label={`Sort by ${label}`}>
            <HeaderLabel>{label}</HeaderLabel>
            <SvgIcon
              name={
                sortField === sortKey && sortDirection === 'ASC'
                  ? SvgNames.ChevronUp
                  : SvgNames.ChevronDown
              }
              width={12}
              height={12}
              color={tokens.TINTS_HERO_BLUE_50}
            />
          </SortButton>
        ) : (
          <HeaderLabel>{label}</HeaderLabel>
        )}
      </EqualCell>
    ))}
    <DotsCell />
  </EqualHeaderRow>
);

type ListViewEqualRowProps = {
  prospect: Prospect;
  columns: ListViewColumnDef[];
  isEven: boolean;
  isFloaterMenuOpen: boolean;
  floaterRef: RefObject<HTMLDivElement>;
  onDotsClick: () => void;
  onRowBodyClick?: () => void;
  actionsAriaLabel: string;
  /** Taller rows for two-line cell text (Referral Center). */
  multiline?: boolean;
  /** Same background on every row (Referral Center). */
  filledBackground?: boolean;
  /** Referral Center: open menu to the left of the dots trigger. */
  floaterPlacement?: FloaterMenuPlacement;
};

type FloaterMenuPlacement = 'end' | 'start' | 'below';

const ListViewEqualRow = ({
  prospect,
  columns,
  isEven,
  isFloaterMenuOpen,
  floaterRef,
  onDotsClick,
  onRowBodyClick,
  actionsAriaLabel,
  multiline = false,
  filledBackground = false,
  floaterPlacement = 'end',
}: ListViewEqualRowProps) => {
  const handleRowClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!onRowBodyClick || isFloaterMenuOpen) {
      return;
    }
    onRowBodyClick();
    event.stopPropagation();
  };

  const handleDotsClick = (event: MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    onDotsClick();
  };

  return (
    <EqualDataRow
      $isEven={isEven}
      $filledBackground={filledBackground}
      $isFloaterMenuOpen={isFloaterMenuOpen}
      $multiline={multiline}
      $clickable={Boolean(onRowBodyClick)}
      onClick={handleRowClick}>
      {columns.map(({ id, renderCell }) => (
        <EqualCell key={id} $multiline={multiline}>
          {renderCell(prospect)}
        </EqualCell>
      ))}
      <DotsCell $multiline={multiline} onClick={handleDotsClick}>
        <button type="button" aria-label={actionsAriaLabel}>
          <SvgIcon
            name={SvgNames.VerticalDotsSvg}
            width={24}
            height={24}
            color={tokens.BRAND_HEROBLUE}
          />
        </button>
        {isFloaterMenuOpen && (
          <ListViewFloaterMenu
            ref={floaterRef}
            prospect={prospect}
            placement={floaterPlacement}
          />
        )}
      </DotsCell>
    </EqualDataRow>
  );
};

type ListViewFixedHeaderProps<TSort extends string> = {
  columns: ListViewColumnDef<TSort>[];
  sortField: string;
  sortDirection: ListViewSortDirection;
  onSort: (columnLabel: string) => void;
  actionsColumnWidth?: number;
};

const ListViewFixedHeader = <TSort extends string>({
  columns,
  sortField,
  sortDirection,
  onSort,
  actionsColumnWidth = 24,
}: ListViewFixedHeaderProps<TSort>) => (
  <FixedHeaderGridRow $isFloaterMenuOpen={false} $isEven={false}>
    {columns.map((column) => (
      <FixedDataCell
        key={column.id}
        style={{ cursor: column.sortKey ? 'pointer' : undefined }}
        onClick={() => column.label && column.sortKey && onSort(column.label)}
        width={column.width || 100}>
        {column.label}{' '}
        {column.sortKey &&
          (sortField === column.label && sortDirection === 'ASC' ? (
            <SvgIcon name={SvgNames.ChevronUp} width="12" height="12" />
          ) : (
            <SvgIcon name={SvgNames.ChevronDown} width="12" height="12" />
          ))}
      </FixedDataCell>
    ))}
    <FixedActionsCell width={actionsColumnWidth} />
  </FixedHeaderGridRow>
);

type ListViewFloaterMenuProps = {
  prospect: Prospect;
  placement?: FloaterMenuPlacement;
};

const ListViewFloaterMenu = forwardRef<
  HTMLDivElement,
  ListViewFloaterMenuProps
>(({ prospect, placement = 'end' }, ref) => (
  <FloaterMenuContainer ref={ref} $placement={placement}>
    <ListViewMenuOptions
      Child={({ onClick, label }) => (
        <ListViewMenuItem onClick={onClick}>
          <Typography>{label}</Typography>
        </ListViewMenuItem>
      )}
      prospect={prospect}
    />
  </FloaterMenuContainer>
));

ListViewFloaterMenu.displayName = 'ListViewFloaterMenu';

const ROW_STYLES = `
  display: flex;
  align-items: center;
  width: 100%;
  border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  min-height: 40px;
  padding: 0;
`;

const EqualHeaderRow = styled.div`
  ${ROW_STYLES}
`;

const EqualDataRow = styled.div<{
  $isEven: boolean;
  $filledBackground?: boolean;
  $isFloaterMenuOpen?: boolean;
  $multiline?: boolean;
  $clickable?: boolean;
}>`
  ${ROW_STYLES}
  align-items: flex-start;
  min-height: ${({ $multiline }) => ($multiline ? '72px' : '40px')};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  ${({ $isEven, $filledBackground }) =>
    ($filledBackground || $isEven) &&
    `
    background-color: rgba(31, 38, 71, 0.04);
  `}

  ${({ $isFloaterMenuOpen }) =>
    $isFloaterMenuOpen &&
    `
    background-color: rgba(165, 201, 255, 0.3);
  `}

  &:last-of-type {
    border-bottom: 1px solid ${tokens.TINTS_OFF_WHITE_40};
  }
`;

const EQUAL_CELL_PADDING_PX = 12;

const EqualCell = styled.div<{ $multiline?: boolean }>`
  flex: 1 1 0;
  min-width: 0;
  padding: ${EQUAL_CELL_PADDING_PX}px;

  ${({ $multiline }) =>
    $multiline &&
    `
    margin: auto 0;
  `}
`;

const DotsCell = styled.div<{ $multiline?: boolean }>`
  flex: 0 0 28px;
  width: 28px;
  padding: ${EQUAL_CELL_PADDING_PX}px 4px;
  display: flex;
  align-items: ${({ $multiline }) => ($multiline ? 'center' : 'flex-start')};
  justify-content: center;
  position: relative;
  cursor: pointer;

  ${({ $multiline }) =>
    $multiline &&
    `
    margin: auto 0;
  `}

  button {
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const SortButton = styled.button`
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  min-width: 0;
`;

const HeaderLabel = styled(Typography)`
  display: block;
  text-align: left;
  color: ${tokens.TINTS_HERO_BLUE_50};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const FixedHeaderGridRow = styled.div<{
  $isFloaterMenuOpen: boolean;
  $isEven: boolean;
}>`
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  padding: 16px 32px;
  border-bottom: 1px solid ${tokens.OFFWHITE_40};
`;

const FixedDataCell = styled(Typography)<{ width?: number }>`
  padding: 12px;
  width: ${(props) => props.width || 100}%;
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
  position: relative;
`;

const FixedActionsCell = styled.div<{ width?: number }>`
  padding: 12px;
  width: ${(props) => props.width || 24}px;
  flex-shrink: 0;
`;

const FloaterMenuContainer = styled.div<{ $placement?: FloaterMenuPlacement }>`
  position: absolute;
  top: 0;
  background-color: ${tokens.WHITE};
  box-shadow: 0 5px 10px rgba(0, 0, 0, 0.3);
  border-radius: 3px;
  border: 1px solid ${tokens.BORDER_LIGHTER};
  z-index: 50;
  width: 280px;
  max-width: min(280px, calc(100vw - 32px));

  ${({ $placement = 'end' }) => {
    if ($placement === 'start') {
      return `
        right: 100%;
        margin-right: 8px;
      `;
    }
    if ($placement === 'below') {
      return `
        top: 100%;
        right: 0;
        margin-top: 4px;
      `;
    }
    return `
      right: 0;
    `;
  }}
`;

const ListViewMenuItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border-radius: 3px;
  cursor: pointer;

  &:hover {
    background-color: rgba(31, 38, 71, 0.04);
  }
`;

export type { FloaterMenuPlacement };
export { ListViewCellText } from 'components/atoms/ListViewCellText';
export {
  FloaterMenuContainer,
  ListViewEqualHeader,
  ListViewEqualRow,
  ListViewFixedHeader,
  ListViewFloaterMenu,
  ListViewMenuItem,
};
